import type { ICvPersonBBox } from '@/helpers/audienceCvPersonTracker'
import { audienceCvPersonConfig, audienceCvTfjsWasmCdnVersion } from '@/helpers/audienceCvPersonConfig'

let loadFailed = false
let modelPromise: Promise<import('@tensorflow-models/coco-ssd').ObjectDetection | null> | null = null

function normalizeBbox(
  bbox: [number, number, number, number],
  videoW: number,
  videoH: number
): ICvPersonBBox {
  const [x0, y0, w, h] = bbox
  if (videoW > 0 && videoH > 0) {
    return {
      x: x0 / videoW,
      y: y0 / videoH,
      w: w / videoW,
      h: h / videoH
    }
  }
  return { x: x0, y: y0, w, h }
}

/**
 * Same-origin WASM first (`public/tfjs-wasm/` from sync script), then jsDelivr fallback.
 */
function tfjsWasmPathPrefixes(): string[] {
  const cdn
    = `https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-backend-wasm@${audienceCvTfjsWasmCdnVersion}/dist/`
  if (typeof window === 'undefined') {
    return [cdn]
  }
  const baseUrl = import.meta.env.BASE_URL ?? '/'
  try {
    const local = new URL('tfjs-wasm/', new URL(baseUrl, window.location.href)).href
    return [local, cdn]
  }
  catch {
    return [cdn]
  }
}

/**
 * Prefer WebGL, then WASM (self-hosted, then CDN), then CPU.
 */
async function ensureTfjsBackend(tf: typeof import('@tensorflow/tfjs')): Promise<boolean> {
  const tryBackend = async (name: string): Promise<boolean> => {
    try {
      return await tf.setBackend(name)
    }
    catch {
      return false
    }
  }
  if (await tryBackend('webgl')) {
    await tf.ready()
    return true
  }
  try {
    const wasm = await import('@tensorflow/tfjs-backend-wasm')
    for (const prefix of tfjsWasmPathPrefixes()) {
      wasm.setWasmPaths(prefix)
      if (await tryBackend('wasm')) {
        await tf.ready()
        return true
      }
    }
  }
  catch {
    // WASM chunk failed — fall through to CPU
  }
  if (await tryBackend('cpu')) {
    await tf.ready()
    return true
  }
  return false
}

/**
 * Lazy-loads COCO-SSD (MobileNet v2) and returns person-class boxes (normalized 0–1).
 * Returns empty array if load fails or video is too small.
 */
export async function detectPersonBoxesFromVideo(
  video: HTMLVideoElement,
  minScore: number
): Promise<ICvPersonBBox[]> {
  if (loadFailed) {
    return []
  }
  const w = video.videoWidth
  const h = video.videoHeight
  const minExt = audienceCvPersonConfig.detect.minVideoExtentPx
  if (w < minExt || h < minExt) {
    return []
  }
  try {
    if (modelPromise == null) {
      modelPromise = (async () => {
        const tf = await import('@tensorflow/tfjs')
        const backendOk = await ensureTfjsBackend(tf)
        if (!backendOk) {
          return null
        }
        const cocoSsd = await import('@tensorflow-models/coco-ssd')
        return cocoSsd.load({ base: 'mobilenet_v2' })
      })()
    }
    const model = await modelPromise
    if (model == null) {
      loadFailed = true
      return []
    }
    const preds = await model.detect(video)
    const out: ICvPersonBBox[] = []
    for (const p of preds) {
      if (p.class !== 'person' || p.score < minScore) {
        continue
      }
      out.push(normalizeBbox(p.bbox, w, h))
    }
    return out
  }
  catch {
    loadFailed = true
    modelPromise = null
    return []
  }
}

/**
 * True after TF.js backend / COCO-SSD load gave up (subsequent calls short-circuit).
 * Used by the audience debug overlay when tenant person-CV is on.
 */
export function isAudienceCvPersonModelUnavailable(): boolean {
  return loadFailed
}

/** For tests / teardown (e.g. player reset). */
export function resetAudienceCvPersonDetectCache(): void {
  loadFailed = false
  modelPromise = null
}
